import React, { useState } from "react";
import getActionPermissions, { ActionPermissions } from "../getActionPermissions";
import Securable from "../Securable";
import { SecurableComponentProps } from "./SecurableComponent";

const SecurableDataStateContext = React.createContext<Securable[] | undefined>(
  []
);
const SecurableDataDispatchContext = React.createContext<
  ((securableData: Securable[]) => void) | undefined
>(undefined);

export function useSecurableData() {
  const context = React.useContext(SecurableDataStateContext);
  if (context === undefined) {
    throw new Error("useSecurableData must be used within a SecurityProvider");
  }

  return context;
}

export function useSecurableDataDispatch() {
  const context = React.useContext(SecurableDataDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useSecurableDataDispatch must be used within a SecurityProvider"
    );
  }

  return context;
}

const SecurityRoleIdsStateContext = React.createContext<number[] | undefined>(
  []
);
const SecurityRoleIdsDispatchContext = React.createContext<
  ((securityRoleIds: number[]) => void) | undefined
>(undefined);

export function useSecurityRoleIds() {
  const context = React.useContext(SecurityRoleIdsStateContext);
  if (context === undefined) {
    throw new Error(
      "useSecurityRoleIds must be used within a SecurityProvider"
    );
  }

  return context;
}

export function useSecurityRoleIdsDispatch() {
  const context = React.useContext(SecurityRoleIdsDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useSecurityRoleIdsDispatch must be used within a SecurityProvider"
    );
  }

  return context;
}

export function useSecurable(
  securableName: string,
  action: undefined | string | string[]
) {
  const props: SecurableComponentProps = {
    securableName,
    action
  };

  return useSecurableActionPermissions(props);
}

export interface SecurableActionPermissions {
  securityRoleIds: number[];
  actionPermissions: ActionPermissions;
}

export function useSecurableActionPermissions(
  props: SecurableComponentProps
): SecurableActionPermissions {
  const implicitSecurityRoleIds = useSecurityRoleIds();
  const implicitSecurableData = useSecurableData();

  const {
    securityRoleIds: rawSecurityRoleIds,
    securableName,
    securableData: rawSecurableData,
    action
  } = props;

  const securableData = rawSecurableData || implicitSecurableData;
  const securityRoleIds = rawSecurityRoleIds || implicitSecurityRoleIds;

  const actionPermissions = getActionPermissions(
    securableData,
    securityRoleIds,
    securableName,
    action
  );

  return {
    securityRoleIds,
    actionPermissions
  };
}

export interface SecurityProviderProps {
  children?: any;
  initialSecurableData?: Securable[];
  initialSecurityRoleIds?: number[];
}

function SecurityProvider(props: SecurityProviderProps) {
  const {
    children,
    initialSecurableData = [],
    initialSecurityRoleIds = []
  } = props;

  const [securableData, setSecurableData] = useState(initialSecurableData);
  const [securityRoleIds, setSecurityRoleIds] = useState(
    initialSecurityRoleIds
  );

  return (
    <SecurableDataStateContext.Provider value={securableData}>
      <SecurableDataDispatchContext.Provider value={setSecurableData}>
        <SecurityRoleIdsStateContext.Provider value={securityRoleIds}>
          <SecurityRoleIdsDispatchContext.Provider value={setSecurityRoleIds}>
            {children}
          </SecurityRoleIdsDispatchContext.Provider>
        </SecurityRoleIdsStateContext.Provider>
      </SecurableDataDispatchContext.Provider>
    </SecurableDataStateContext.Provider>
  );
}

export default SecurityProvider;
